/**
 * Returns a short, human‑readable string for how long ago the timestamp
 * occurred.
 *
 * Accepts either **milliseconds** or **nanoseconds**.
 *
 * ```ts
 * formatElapsedTime(Date.now() - 4_500);          // "10 sec"
 * formatElapsedTime(Date.now() - 45_000);         // "1 min"
 * formatElapsedTime(Date.now() * 1e6 - 3e9);      // "10 min"  // nanoseconds
 * ```
 */
export const formatElapsedTime = (ts: number): string => {
  // accept both ms ou ns, normalise to ms
  const ms = ts > 1e14 ? ts / 1e6 : ts;

  // convert to seconds
  const elapsed = Math.max(0, Date.now() - ms) / 1000;

  const pluralUnit = ['day', 'month', 'year'];

  const buckets = [
    { unit: 'sec', limit: 60, div: 1 }, // < 1 min
    { unit: 'min', limit: 3600, div: 60 }, // < 1 h
    { unit: 'h', limit: 86_400, div: 3600 }, // < 1 day
    { unit: 'day', limit: 2_592_000, div: 86_400 }, // < 30 days
    { unit: 'month', limit: 31_536_000, div: 2_592_000 }, // < 12 months
    { unit: 'year', limit: Infinity, div: 31_536_000 }, // 1+ years
  ];

  if (elapsed > 1) {
    for (const { limit, div, unit } of buckets) {
      if (elapsed < limit) {
        const val = Math.round(elapsed / div) || 1;
        const plural = val > 1 && pluralUnit.includes(unit) ? 's' : '';
        return `${val} ${unit}${plural}`;
      }
    }
  }

  return 'just now';
};
